import type { ReactNode } from "react";
import { Link, useMatch } from "react-router-dom";
import { buttonClasses } from "@dearbill/shared";
import { useOrg } from "../../shell/OrgContext";
import { usePharmacyOnboarding } from "./PharmacyOnboardingContext";

export function PharmacyOnboardingLink(): ReactNode {
  const { orgId } = useOrg();
  const onboarding = usePharmacyOnboarding();
  if (!onboarding) return null;
  return (
    <Link
      to={`/orgs/${orgId}/get-started`}
      className={buttonClasses(
        "ghost",
        "md",
        "min-h-11 max-w-full whitespace-normal px-3 py-3 text-base [overflow-wrap:anywhere]",
      )}
    >
      Getting started
    </Link>
  );
}

export function PharmacyOnboardingInvitation(): ReactNode {
  const { orgId } = useOrg();
  const onboarding = usePharmacyOnboarding();
  if (!onboarding || onboarding.loading || onboarding.progress?.status === "completed") return null;
  const resume = onboarding.progress !== null && onboarding.progress.status !== "not_started";
  return (
    <section
      aria-labelledby="getting-started-invitation"
      className="border-haze bg-ice flex flex-wrap items-center justify-between gap-4 rounded-2xl border p-5"
    >
      <div className="min-w-0 flex-1 basis-72">
        <h2 id="getting-started-invitation" className="text-h4 text-midnight">
          {resume ? "Pick up where you left off" : "Get started with your pharmacy"}
        </h2>
        <p className="text-slate-teal mt-2 text-base leading-relaxed">
          Check pharmacy setup, learn how to help patients, and choose your next step.
        </p>
      </div>
      <Link
        to={`/orgs/${orgId}/get-started`}
        className={buttonClasses("secondary", "md", "min-h-11 whitespace-normal text-base")}
      >
        {resume ? "Resume introduction" : "Start introduction"}
      </Link>
    </section>
  );
}

/** A task can leave the introduction for the real profile form without losing its place. */
export function PharmacyOnboardingResume(): ReactNode {
  const { orgId } = useOrg();
  const onboarding = usePharmacyOnboarding();
  const onIntroduction = useMatch("/orgs/:orgId/get-started");
  const onHome = useMatch("/orgs/:orgId/home");
  if (
    !onboarding ||
    onIntroduction ||
    onHome ||
    !["in_progress", "deferred"].includes(onboarding.progress?.status ?? "")
  )
    return null;
  return (
    <div className="border-haze bg-ice flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 text-base [overflow-wrap:anywhere] sm:px-7">
      <span>Your introduction is saved. Return when you are ready.</span>
      <Link
        to={`/orgs/${orgId}/get-started`}
        className="text-ember inline-flex min-h-11 items-center font-semibold underline underline-offset-2"
      >
        Resume introduction
      </Link>
    </div>
  );
}
